import {useState, useEffect, useMemo} from 'react';
import {Form, Button, Row, Col} from 'react-bootstrap';
import {usePortalContext} from '../../lib/context/portalContext';
import withAlerts from '../../lib/withAlerts';
import {FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faPlus, faXmark} from '@fortawesome/pro-light-svg-icons';
import {compose} from '../../lib/compose';
import Modal from 'react-bootstrap/Modal';
import { useForm, useFieldArray } from "react-hook-form";
import { gql } from "@apollo/client";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import WhitelistConfirmModal from './confirmWhitelistModal';
import DeleteConfirmModal from '../deleteConfirmModal';
import {useWhitelistMutations} from './whitelistMutations';
import cx from 'classnames';
import {useCustomerContext} from '../../lib/context/customerContext';
import {Can} from '../auth/can';

export const EditWhitelistModal = (props) => {
  const {rowData, togglePortal} = usePortalContext();
  const {success, error} = props.alerts;
  const {remove: removelist, edit} = useWhitelistMutations();
  const [duplicateName, setDuplicateName] = useState(false);
  const [getWhitelistNames] = useLazyQuery(WHITELISTS);
  const {hasPermission} = useCustomerContext();

  // RBAC: SSID_LIST_EDIT - "On off-canvas, disable all text fields and
  // dropdown lists, and hide the 'Add SSID' link; Disable 'Submit' button."
  // A <fieldset disabled> covers the text fields/dropdowns (native form
  // controls). It does NOT cover the "Add SSID" link or the per-row remove
  // ('x') icon, since those are plain <span onClick> handlers, not form
  // controls - those need explicit gating below. The sheet only explicitly
  // names "Add SSID" for hiding; the per-row remove icon isn't mentioned,
  // but is folded into the same gate as an inference (leaving it live while
  // text fields are locked would be inconsistent) - flag if that's wrong.
  const listEditDisabled = !hasPermission('SSID_LIST_EDIT');
  
  const {data: customerAccounts} = useQuery(CUSTOMER_ACCOUNTS);
  const accounts = customerAccounts?.CustomerAccounts?.result || [];
  const handleClose = () => togglePortal();

  const ssidsFilters = useMemo(() => ({
    field: "whitelist_ssid_list_id", value: `${rowData?.id}`
  }), [rowData?.id]);

  const {data, loading} = useQuery(SSIDS, {
    variables: { filters: ssidsFilters },
    skip: !rowData?.id
  });

  const whitelistResult = data?.SSIDs?.result || [];
  
  const { register, control, trigger, handleSubmit, reset, setFocus, watch, formState: {errors} } = useForm({
    values:  {
      name: rowData?.name,
      ssids: whitelistResult
    },
    mode: 'onBlur'
  });

  const dirtyForm = Object.keys(errors)?.length !== 0;

  const uniqueSsid = (fields) => {
    const ssidSet = new Set(fields.map((field) => field.ssid));
    return ssidSet.size === fields.length ? true : 'Duplicate ssid found';
  };

  const {
    fields,
    append,
    remove
  } = useFieldArray({
    control,
    name: "ssids",
    rules: {
      validate: uniqueSsid
    }
  });

  useEffect(() => {
    loading === false && reset();
  }, [loading]);
  
  const handleRemoveWhitelist = async () => {
    const removeWhitelist = await removelist({
      variables: {
        whitelist_ssid_list_id: rowData?.id
      }
    });

    if(removeWhitelist?.errors) {
      const userFacingError = ('Wi-Fi SSID List deletion failed.');
      console.error(userFacingError + JSON.stringify(removeWhitelist?.errors));
      error({msg: userFacingError});
    } else {
      togglePortal();
      success({msg: 'Wi-Fi SSID List deleted.'});
    }
  };

  const nameCheck = async (name) => {
    if(rowData?.name === name || name === '') {
      return;
    }

    const check = await getWhitelistNames({
      variables: {
        filters: {
          field: "name",
          value: name
        }
      }
    })
    
    if(check?.data?.Whitelists) {
      setDuplicateName(true);
      setFocus('name')
      return;
    } else {
      setDuplicateName(false);  
    }
  }

  const handleWhitelist = async (formData) => {
    const name = formData?.name || "";
    
    const filtered = JSON.stringify(formData?.ssids?.filter((pair) => {
        return pair.ssid != null && pair.ssid != "";
    }).map((el) => el));

    const whitelistInput = {
      name: name,
      whitelist_ssid_list_id: rowData?.id,
      SSIDs: filtered
    };

    const updateWhitelist = await edit({variables: {whitelist: whitelistInput}});
    if(updateWhitelist.errors) {
      error({msg: "Wi-Fi SSID List edit failed."});
    } else {
      success({msg: `Wi-Fi SSID List updated.`});
      reset();
      togglePortal();
    }
  }

  const max8 = watch("ssids")?.length === 8;
  if(loading) return null;

  return (
    <Form className="mb-3" onSubmit={handleSubmit(handleWhitelist)}>
      <Modal.Header closeButton>
        <Modal.Title>Edit List</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <fieldset disabled={listEditDisabled}>
        <div className="modal-form-white">
          <Row>
            <Col sm="6">
              <Form.Group className="mb-5">
                <Form.Label>List Name</Form.Label>
                <Form.Control
                {...register("name", {
                  onBlur: (e) => nameCheck(e.target.value),
                  required: true,
                  maxLength: {
                    value: 50,
                    message: "Length of List Name exceeds 50 characters."
                  }
                })}
                type="text"
                placeholder="Name"
                className={cx({'error-input': duplicateName || errors.name })}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              {fields.map((item, i) => {
                return (
                  <Row className="mb-3" key={`row_${i}`}>
                    <Col sm="6">
                      <Form.Label>SSID</Form.Label>
                      <Form.Group className="ssid-input mb-3" key={`whitelist_${i}`}>
                        <Form.Control
                          {...register(`ssids.${i}.ssid`, {
                            onBlur: () => trigger('ssids')
                          })}
                          id={item?.id}
                          type="text"
                          placeholder="SSID..."
                          control={control}
                          className={cx({'error-input': errors?.ssids})}
                          />
                      </Form.Group>    
                    </Col>
                    <Col sm="6">
                      <Row className="align-items-center">
                        <Col sm="11">
                          <Form.Label>Site Description</Form.Label>
                          <Form.Select
                            {...register(`ssids.${i}.site_account_id`)}
                            control={control}>
                            { accounts.map(({id, description}) => (
                              <option key={id} value={id}>{description}</option>
                              ))}
                          </Form.Select>
                        </Col>
                        <Col sm="1">
                        <Form.Label></Form.Label>
                          {/* RBAC: not explicitly named in the sheet - see
                              note above on listEditDisabled */}
                          {!listEditDisabled &&
                            <span
                            className="privoro-gray pointer d-block mt-2"
                            onClick={() => {remove(i)}}
                            onKeyDown={() => {remove(i)}}>
                          <FontAwesomeIcon icon={faXmark} size="sm" /></span>
                          }
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                )
              })}
            </Col>
          </Row>
          {/* RBAC: SSID_LIST_EDIT - explicitly "hide the 'Add SSID' link" */}
          {!max8 && !listEditDisabled && <Row className="mb-3">
            <Col>
              <span
                className="privoro-gray"
                onClick={() => {append({ssid: "", site_account_id: rowData?.site_account_id})}}
                onKeyDown={() => {append({ssid: "", site_account_id: rowData?.site_account_id})}}>
                  <FontAwesomeIcon icon={faPlus} size="sm" /> <span className="link-span">Add SSID</span></span>
            </Col>
          </Row>}
        </div>
        </fieldset>

        {errors.ssids &&
          <div className="d-grid gap-2">
            <strong className="privoro-black">SSIDs must be unique.<br/></strong>
          </div>
        }

        {errors.name && errors.name.type === "required" &&
          <div className="d-grid gap-2">
            <strong className="privoro-black">List name is required.<br/></strong>
          </div>
        }
        {errors?.name && errors.name.type === "maxLength" &&
          <div className="d-grid gap-2">
            <strong className="privoro-black">{errors?.name?.message}<br/></strong>
          </div>
        }

        {duplicateName &&
          <div className="d-grid gap-2">
            <strong className="privoro-black">List name already exists.<br/></strong>
          </div>
        }
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end">
          {/* RBAC: SSID_LIST_DELETE - hidden (not disabled) to match the
              convention used everywhere else in the app (Policy, HDM,
              User delete all hide the link/button). The sheet's literal
              wording said "disable," but DeleteConfirmModal doesn't even
              forward a `disabled` prop to its Button, and the team's
              standing convention is hide - so gating via <Can> here
              instead. */}
          <Can code="SSID_LIST_DELETE">
            <DeleteConfirmModal
              onChange={handleRemoveWhitelist}
              scope={"ssid_whitelist"} 
              message={"Are you sure you want to delete this list?"}
              title="Delete List"
              button
              buttontext="Delete List"
              variant="secondary" />
          </Can>

          <Button onClick={handleClose} className="ms-5" variant="secondary">Cancel</Button> 
          <WhitelistConfirmModal disabled={dirtyForm || listEditDisabled} onChange={handleSubmit(handleWhitelist)} />
        </div>
      </Modal.Footer>
    </Form>
  );
}


const SSIDS = gql`
  query getSSIDs($filters: GenericFilters!) {
    SSIDs(filters: $filters) {
      result { id ssid site_account_id }
    }
  }
`;

const WHITELISTS = gql`
  query getWhitelists($filters: GenericFilters!) {
    Whitelists(filters: $filters) {
      result { name }
    }
  }
`;

const CUSTOMER_ACCOUNTS = gql`
  query getSiteLocations($filters: GenericFilters) {
    CustomerAccounts(filters: $filters) {
      result { id name description root parent }
    }
  }
`;

export default compose(
  withAlerts,
)(EditWhitelistModal);
